import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CopyButton } from '../../../../ui/copy-button';
import { Badge } from '../../../../ui/badge';
import { ICellRendererParams } from 'ag-grid-community';
import { useAttributionForAddress } from 'src/hooks/attributions/useAttributionForAddress';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { LockedAttributionHoverCard } from 'src/components/Attribution/LockedAttributionHoverCard';
import { isLockedAttribution } from 'src/components/Address/utils';

type Props = ICellRendererParams & {
  columnId: string;
  to_entity: string | null | ((params: ICellRendererParams) => string | null);
  from_entity: string | null | ((params: ICellRendererParams) => string | null);
};

const getBadgeLabel = (text: string, maxLength: number = 20) => {
  if (text.length <= maxLength) {
    return { label: text, isTruncated: false };
  }
  return {
    label: `${text.slice(0, maxLength)}...`,
    isTruncated: true,
  };
};

export const AddressCellRenderer = (props: Props) => {
  const value = props.value;
  const shortened = `${value.slice(0, 5)}...${value.slice(-5)}`;
  const { id: address } = useParams<{ id: string }>();
  const { chain } = useAddressAndChainParams();

  // Get attribution data for this address
  const { attribution } = useAttributionForAddress(chain, value);

  const isCurrentAddress =
    address && value.toLowerCase() === address.toLowerCase();

  // Get entity value - prefer attribution over props
  const getEntityValue = () => {
    // First check if we have attribution data
    if (attribution?.entity) {
      return attribution.entity;
    }

    // Fall back to props-based entity
    const entityProp =
      props.columnId === 'to' ? props.to_entity : props.from_entity;
    if (typeof entityProp === 'function') {
      return entityProp(props);
    }
    return entityProp;
  };

  const entityValue = getEntityValue();

  const renderContent = () => {
    // Scenario 3: Entity exists and is NOT locked - show badge + address
    if (entityValue && !isLockedAttribution(entityValue)) {
      const { label: badgeLabel, isTruncated } = getBadgeLabel(entityValue);

      const badgeElement = (
        <Badge variant={isCurrentAddress ? 'outline' : 'primary'}>
          {badgeLabel}
        </Badge>
      );

      return (
        <div className="flex items-center gap-2">
          <div className="w-[220px]">
            <Tooltip>
              <TooltipTrigger>
                {isTruncated ? (
                  <Tooltip>
                    <TooltipTrigger>{badgeElement}</TooltipTrigger>
                    <TooltipContent>
                      <p>{entityValue}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  badgeElement
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{value}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <CopyButton
            text={value}
            className="flex h-5 w-5 items-center justify-center"
            iconSize={14}
          />
        </div>
      );
    }

    // Scenario 1 & 2: No entity OR locked entity - show just address
    // If locked, also show lock icon
    return (
      <div className="flex items-center gap-2">
        <div className="inline-block w-[220px]">
          <Tooltip>
            <TooltipTrigger>
              <span>{shortened}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{value}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {entityValue && isLockedAttribution(entityValue) && (
          <LockedAttributionHoverCard
            attributionValue={entityValue}
            attributionType="entity"
            iconSize="sm"
            triggerClassName="ml-1"
            fallback={null}
          />
        )}
        <CopyButton
          text={value}
          className="flex h-5 w-5 items-center justify-center"
          iconSize={14}
        />
      </div>
    );
  };

  return isCurrentAddress ? (
    renderContent()
  ) : (
    <Link
      to={`/${chain}/address/${value}`}
      className="text-[#0584C3] hover:underline"
    >
      {renderContent()}
    </Link>
  );
};
